import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import {
  hasConsentChoice,
  acceptAllCookies,
  acceptEssentialOnly,
  saveCookieConsent,
  getCookieConsent,
  COOKIE_CATEGORIES,
} from "@helper/cookieConsent";
import {
  saveCookieConsentToBackend,
  updateCookieConsentOnBackend,
} from "../../services/cookieConsentCrud";

const CookieConsentBanner = () => {
  const { t } = useTranslation();
  const { auth } = useSelector((state) => state);
  const isAuthenticated = auth?.authToken != null;

  const [open, setOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    [COOKIE_CATEGORIES.ESSENTIAL]: true,
    [COOKIE_CATEGORIES.ANALYTICS]: false,
    [COOKIE_CATEGORIES.MARKETING]: false,
    [COOKIE_CATEGORIES.PREFERENCES]: false,
  });

  useEffect(() => {
    // Check if user has already made a consent choice
    if (!hasConsentChoice()) {
      setOpen(true);
    } else {
      // Load existing preferences
      const existingConsent = getCookieConsent();
      if (existingConsent) {
        setPreferences(existingConsent);
      }
    }
  }, []);

  const handleAcceptAll = async () => {
    const consent = acceptAllCookies();
    setPreferences(consent);
    setOpen(false);

    // Sync with backend if user is authenticated
    if (isAuthenticated) {
      try {
        await saveCookieConsentToBackend(consent);
      } catch (error) {
        console.warn("Failed to save cookie consent to backend:", error);
      }
    }
  };

  const handleAcceptEssential = async () => {
    const consent = acceptEssentialOnly();
    setPreferences(consent);
    setOpen(false);

    // Sync with backend if user is authenticated
    if (isAuthenticated) {
      try {
        await saveCookieConsentToBackend(consent);
      } catch (error) {
        console.warn("Failed to save cookie consent to backend:", error);
      }
    }
  };

  const handleCustomize = () => {
    setShowDetails(true);
  };

  const handleSavePreferences = async () => {
    saveCookieConsent(preferences);
    setOpen(false);

    // Sync with backend if user is authenticated
    if (isAuthenticated) {
      try {
        await updateCookieConsentOnBackend(preferences);
      } catch (error) {
        console.warn("Failed to update cookie consent on backend:", error);
      }
    }
  };

  const handleTogglePreference = (category) => {
    if (category === COOKIE_CATEGORIES.ESSENTIAL) {
      return; // Essential cookies cannot be disabled
    }
    setPreferences((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const cookieCategories = [
    {
      key: COOKIE_CATEGORIES.ESSENTIAL,
      title: t("cookie_consent.essential_cookies"),
      description: t("cookie_consent.essential_cookies_description"),
      required: true,
    },
    {
      key: COOKIE_CATEGORIES.ANALYTICS,
      title: t("cookie_consent.analytics_cookies"),
      description: t("cookie_consent.analytics_cookies_description"),
      required: false,
    },
    {
      key: COOKIE_CATEGORIES.MARKETING,
      title: t("cookie_consent.marketing_cookies"),
      description: t("cookie_consent.marketing_cookies_description"),
      required: false,
    },
    {
      key: COOKIE_CATEGORIES.PREFERENCES,
      title: t("cookie_consent.preferences_cookies"),
      description: t("cookie_consent.preferences_cookies_description"),
      required: false,
    },
  ];

  if (!open) {
    return null;
  }

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          position: "fixed",
          bottom: 0,
          m: 0,
          borderRadius: "16px 16px 0 0",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: "#286CA5",
          color: "#fff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 2,
        }}
      >
        <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
          {t("cookie_consent.banner_title")}
        </Typography>
        {showDetails && (
          <IconButton
            onClick={() => setShowDetails(false)}
            sx={{ color: "#fff" }}
            aria-label="close details"
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {!showDetails ? (
          <Box>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t("cookie_consent.banner_description")}
            </Typography>
          </Box>
        ) : (
          <Box>
            <Typography variant="body2" sx={{ mb: 3, color: "#6c757d" }}>
              {t("cookie_consent.settings_description")}
            </Typography>

            {cookieCategories.map((category, index) => (
              <Accordion
                key={category.key}
                defaultExpanded={index === 0}
                sx={{
                  mb: 1,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    backgroundColor: "#f8f9fa",
                    borderRadius: "8px",
                    "&.Mui-expanded": {
                      minHeight: 48,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "100%",
                      pr: 2,
                    }}
                  >
                    <Typography sx={{ fontWeight: 500 }}>{category.title}</Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={preferences[category.key]}
                          disabled={category.required}
                          onChange={() => handleTogglePreference(category.key)}
                          onClick={(e) => e.stopPropagation()}
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": {
                              color: "#286CA5",
                            },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                              backgroundColor: "#286CA5",
                            },
                          }}
                        />
                      }
                      label=""
                      sx={{ mr: 0 }}
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" sx={{ color: "#6c757d" }}>
                    {category.description}
                  </Typography>
                  {category.required && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        mt: 1,
                        color: "#286CA5",
                        fontWeight: 500,
                      }}
                    >
                      {t("required")} - {t("cookie_consent.essential_cookies")}
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2, gap: 1 }}>
        {!showDetails ? (
          <>
            <Button
              onClick={handleAcceptEssential}
              variant="outlined"
              sx={{
                color: "#286CA5",
                borderColor: "#286CA5",
                "&:hover": {
                  borderColor: "#08424e",
                  backgroundColor: "rgba(40, 108, 165, 0.04)",
                },
              }}
            >
              {t("cookie_consent.accept_essential")}
            </Button>
            <Button
              onClick={handleCustomize}
              variant="outlined"
              sx={{
                color: "#286CA5",
                borderColor: "#286CA5",
                "&:hover": {
                  borderColor: "#08424e",
                  backgroundColor: "rgba(40, 108, 165, 0.04)",
                },
              }}
            >
              {t("cookie_consent.customize")}
            </Button>
            <Button
              onClick={handleAcceptAll}
              variant="contained"
              sx={{
                backgroundColor: "#286CA5",
                color: "#fff",
                "&:hover": {
                  backgroundColor: "#08424e",
                },
              }}
            >
              {t("cookie_consent.accept_all")}
            </Button>
          </>
        ) : (
          <Button
            onClick={handleSavePreferences}
            variant="contained"
            fullWidth
            sx={{
              backgroundColor: "#286CA5",
              color: "#fff",
              "&:hover": {
                backgroundColor: "#08424e",
              },
            }}
          >
            {t("cookie_consent.save_preferences")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CookieConsentBanner;
